// Subscribes to the SDK's `client.onMessage(...)` callback and writes
// each decrypted application message into the host-supplied
// `MessagesPersister`. Bypasses packages/ui's syncEngine entirely
// because:
//   1. syncEngine's start/stop dance has a race against React
//      StrictMode's double-mount that leaves `state.client` null
//      while the SDK still has the onMessage handler registered.
//   2. The SDK emits `IncomingMessage` (Uint8Array fields,
//      snake_case) and syncEngine's parseEnvelope was authored
//      against a hypothetical pre-SDK shape; we adapt at the
//      boundary here instead of indirecting through that module.
//
// Writes are idempotent: keyed by `crypto.randomUUID()` for the row
// id, but a `getByContentHash` lookup is run first to skip when the
// same envelope is delivered twice (BE retry, WS reconnect cursor
// replay, etc). On web, the `&contentHash` unique index in Dexie
// would reject duplicates anyway; the up-front lookup keeps the log
// clean and behaves uniformly on hosts whose persistence layer does
// not enforce a unique index.
//
// This module was lifted from
// apps/web/src/features/chat/useIncomingMessages.tsx so apps/desktop
// can share the same realtime ingestion path. Storage access is
// injected through the `MessagesPersister` / `ConversationsPersister`
// interfaces so packages/ui itself does not pin to either platform's
// persistence layer.

import { useEffect, useRef } from 'react';
import { groupNamePayloadToConversationPatch } from './groupNameHydration';
import {
  getCurrentUserAccountId,
  getCurrentUserDisplayName,
  isSelfDeviceIdHex,
  isSelfMlsUserIdHex,
  registerCurrentUserMlsUserId,
} from './registerCurrentUser';
import { resolveMediaUrl } from './mediaUrlResolver';
import { mutationSenderIsAuthor } from './mutationAuthorship';
import { withAvatarCacheKey } from './avatarCacheBust';
import { isDeviceGroupConversationId } from './deviceGroup';
import { isRegisteredCallGroup } from '../calls/callGroupRegistry';
import type { IncomingNotification } from '../notifications/types';
import { useSession } from '../messaging/sessionStore';
import { log } from '../log/log';
import { lastMessagePreview } from './lastMessagePreview';
import type {
  ConversationRecord,
  ConversationsPersister,
  MeetingEventsPersister,
  MessageRecord,
  MessagesPersister,
  NotesPersister,
  PollVotesPersister,
  ReactionsPersister,
  ReadPointersPersister,
  RemindersPersister,
  SavedPersister,
  StarredPersister,
  TasksPersister,
  TourSeenPersister,
} from './persistenceAdapters';

// Mirror of the SDK's `IncomingMessage` (ping-openmls-sdk
// `sdk/js/src/types.ts`). Bytes fields can arrive as either
// `Uint8Array` (when the SDK uses the bytes-as-Uint8Array
// serializer) or `number[]` (default `serde_wasm_bindgen` behaviour
// for `Vec<u8>` with `serde_bytes`). We accept both shapes and
// normalize at the boundary.
type ByteLike = Uint8Array | number[];

interface IncomingMessageLike {
  conversation_id: ByteLike;
  sender_device: ByteLike;
  /** Account-level MLS UserId of the sender (shared across the sender's linked
   *  devices). Present from ping-openmls-sdk ≥ the shared-identity build; used
   *  for bulletproof, fetch-free self-attribution (`isSelfMlsUserIdHex`). */
  sender_user_id?: ByteLike;
  epoch: number | bigint;
  hlc: { wall_ms: number | bigint; logical: number };
  plaintext: ByteLike;
  content_hash: ByteLike;
}

interface ClientWithOnMessage {
  onMessage?(handler: (msg: unknown) => void): () => void;
  /** SDK conversation accessor. Used to emit the delivery-ack envelope
   * back to the conversation right after a peer message is persisted.
   * The SDK exposes send only on the per-conversation proxy, not on
   * MessagingClient itself. */
  getConversation?(id: Uint8Array): { send(plaintext: Uint8Array): Promise<unknown> };
}

export interface UseIncomingMessagesOptions {
  readonly messages: MessagesPersister;
  readonly conversations: ConversationsPersister;
  /** Optional reaction projector. When supplied, incoming
   * `com.ping.reaction` / `com.ping.reaction_remove` envelopes are
   * upserted/removed in the host's reactions store instead of being
   * dropped. Omit it and those envelopes stay a no-op (preserving the
   * behaviour of call sites that don't yet persist reactions). */
  readonly reactions?: ReactionsPersister;
  /** Optional poll-vote projector. When supplied, incoming
   * `com.ping.poll_vote` envelopes are upserted into the host's
   * poll-votes OR-set (single-choice, latest-wins per actor). Omit it
   * and those envelopes stay a no-op. */
  readonly pollVotes?: PollVotesPersister;
  /** Optional meeting-events store (§2.13). When supplied, incoming
   * `com.ping.meeting` / `_update` / `_rsvp` / `_cancel` envelopes are captured
   * (keyed on the MLS-authenticated sender account) so the host can fold live
   * meeting + RSVP state via `reduceMeeting`. Omit it and meeting sidecars are
   * still suppressed (no stray bubble) but no live state is projected. */
  readonly meetingEvents?: MeetingEventsPersister;
  /** Optional saved-messages store (§2.7 `0x62`/`0x63`, per-conversation). When
   * supplied, incoming `com.ping.save`/`com.ping.unsave` from the user's OWN
   * devices converge the bookmark set in the BACKGROUND (not only while the
   * originating room is mounted). */
  readonly saved?: SavedPersister;
  /** Optional starred-messages store (§2.7 `0x64`, DeviceGroup-only). When
   * supplied, incoming `com.ping.star` `{target, starred}` on the user's own
   * DeviceGroup applies so a star set on another device mirrors here. */
  readonly starred?: StarredPersister;
  /** Optional personal-tasks store (§2.10). When supplied, incoming
   * `com.ping.task_upsert` / `com.ping.task_delete` envelopes are
   * applied to the host's tasks store. These are DeviceGroup-only
   * personal state; see the apply note in `persistIncoming`. */
  readonly tasks?: TasksPersister;
  /** Optional personal-notes store (§2.10). When supplied, incoming
   * `com.ping.note_upsert` / `com.ping.note_delete` envelopes are
   * applied to the host's notes store. DeviceGroup-only personal state. */
  readonly notes?: NotesPersister;
  /** Optional activity-reminders store (§2.10 `0x94`/`0x95`). When supplied,
   * incoming `com.ping.reminder_set` / `com.ping.reminder_clear` envelopes on
   * the user's own DeviceGroup are applied so a reminder set on another device
   * mirrors here. DeviceGroup-only personal state. */
  readonly reminders?: RemindersPersister;
  /** Optional read-pointers store (§2.10 `0x9A`). When supplied, incoming
   * `com.ping.read_pointer` envelopes on the user's own DeviceGroup advance the
   * host's read pointer so Activity unread + chat unread stay cross-device
   * consistent. DeviceGroup-only personal state. */
  readonly readPointers?: ReadPointersPersister;
  /** Optional product-tour-seen store (§2.10 `0x96`). When supplied, incoming
   * `com.ping.tour_seen` envelopes on the user's own DeviceGroup mark the tour
   * seen locally so a tour completed on another device is suppressed here.
   * DeviceGroup-only personal state. */
  readonly tourSeen?: TourSeenPersister;
  /** Optional notification sink. Invoked once per NEW inbound application
   * message (not for delivery/read/vote receipts), AFTER the row + unread
   * bump are persisted. The platform decides whether to actually alert. */
  readonly onNotify?: (evt: IncomingNotification) => void;
  /** Invoked when a `com.ping.self_profile` signal arrives on the user's own
   * DeviceGroup — i.e. one of the account's OTHER devices changed the shared
   * profile (display name / avatar). The host re-fetches `/v1/me/profile` so the
   * change reflects in realtime instead of only on the next foreground. */
  readonly onSelfProfileChanged?: () => void;
  /** Invoked when a `com.ping.conv_state` patch arrives on the user's own
   * DeviceGroup — another of the account's devices changed a conversation's
   * personal state (mute/archive/pin/favorite/delete/folder). The host applies
   * the `patch` to its local conversation row so the change mirrors here. */
  readonly onConvStatePatch?: (
    conversationId: string,
    patch: Record<string, unknown>,
    /** The event's HLC wall-clock (`sentAt`) — a monotonic ordering key so the
     *  host can drop a stale out-of-order patch instead of flip-flopping the
     *  conversation's archive/pin/delete/left state. `0` when unknown (legacy). */
    eventTs: number,
  ) => void;
}

// ── Out-of-order mutation buffer ─────────────────────────────────────────────
//
// An `edit` / `redact` targets another message by its content hash. If the
// mutation arrives BEFORE its target (network reordering, or a fast
// edit/redact-after-send), the target row doesn't exist yet — previously the
// mutation was silently DROPPED, so that member would never see the edit /
// would keep seeing a message the sender deleted-for-everyone. We instead BUFFER
// the mutation and apply it the moment the target message is persisted, so an
// edit/redaction is never lost and every member converges to the same view.

interface PendingMutation {
  readonly kind: 'edit' | 'redact';
  readonly body?: string;
  readonly sentAt: number;
  /** MLS-authenticated sender identity captured at buffer time, so authorship
   *  is verified against the target the moment it lands (the mutation may
   *  arrive before its target — network reordering — and an unauthorized one
   *  must never win a race with the real message). */
  readonly senderDeviceHex: string;
  readonly senderUserIdHex?: string | undefined;
}

// Receive-side authorship gate for edit/redact lives in a standalone,
// harness-free module so the security-critical logic is directly unit-testable.


// Keyed by `${conversationId} ${targetContentHash}`. Module-scoped so it
// survives across the many `persistIncoming` calls in one sync batch. Bounded so
// a mutation whose target never arrives can't grow it without limit.
const pendingMutations = new Map<string, PendingMutation[]>();
const MAX_PENDING_MUTATION_TARGETS = 1000;

function pendingKey(conversationId: string, targetHash: string): string {
  return `${conversationId} ${targetHash}`;
}

function bufferMutation(
  conversationId: string,
  targetHash: string,
  mutation: PendingMutation,
): void {
  const key = pendingKey(conversationId, targetHash);
  const list = pendingMutations.get(key) ?? [];
  list.push(mutation);
  pendingMutations.set(key, list);
  // Evict the oldest target if we're over the cap (insertion order = age).
  if (pendingMutations.size > MAX_PENDING_MUTATION_TARGETS) {
    const oldest = pendingMutations.keys().next().value;
    if (oldest !== undefined) pendingMutations.delete(oldest);
  }
}

/** Apply any buffered edits/redacts for a freshly-persisted message. A redact
 *  wins over edits; otherwise the latest (highest `sentAt`) edit wins.
 *  Each buffered mutation is authorship-verified against the now-present target
 *  (`mutationSenderIsAuthor`) before it can apply — an out-of-order mutation
 *  forged by a non-author is dropped here just as an in-order one is. */
async function applyPendingMutations(
  messages: MessagesPersister,
  conversations: ConversationsPersister,
  conversationId: string,
  targetHash: string,
  target: { id: string; senderAccountId: string; senderUserIdHex?: string },
): Promise<void> {
  const key = pendingKey(conversationId, targetHash);
  const buffered = pendingMutations.get(key);
  // Fast path: no buffered mutation for this target — the overwhelming common
  // case. Return BEFORE touching the DB so a normal message never incurs an
  // extra `conversations.get` just to authorize a (nonexistent) buffered mutation.
  if (buffered === undefined || buffered.length === 0) return;
  const conv = await conversations.get(conversationId);
  pendingMutations.delete(key);
  const authored = buffered.filter((m) =>
    mutationSenderIsAuthor(target, m.senderDeviceHex, m.senderUserIdHex, conv),
  );
  if (authored.length < buffered.length) {
    log.warn('incomingMessages.mutation.buffered.rejected_unauthorized', {
      targetHashPrefix: targetHash.slice(0, 8),
      dropped: buffered.length - authored.length,
    });
  }
  if (authored.length === 0) return;
  try {
    if (authored.some((m) => m.kind === 'redact')) {
      await messages.delete(target.id);
      return;
    }
    const latestEdit = authored
      .filter((m) => m.kind === 'edit' && m.body !== undefined)
      .sort((a, b) => b.sentAt - a.sentAt)[0];
    if (latestEdit?.body !== undefined) {
      await messages.update(target.id, { body: latestEdit.body, editedAt: latestEdit.sentAt });
    }
  } catch {
    /* best-effort — a failed apply just leaves the message unmutated */
  }
}

export function useIncomingMessages(opts: UseIncomingMessagesOptions): void {
  const { client } = useSession();

  // Keep the latest options (persisters + host callbacks) in a ref so the
  // `onMessage` subscription below (re)registers ONLY when the SDK `client`
  // changes — not on every render. The web/desktop hosts pass fresh inline
  // `onConvStatePatch` / `onSelfProfileChanged` closures each render; when
  // those were listed as effect dependencies the SDK handler was town down
  // and re-registered on every parent render, spamming subscribe/unsubscribe
  // and leaving a small window where an inbound envelope could be dropped
  // between the two (torn down, then re-registered). Reading through the ref
  // keeps every callback current without re-subscribing.
  const optsRef = useRef(opts);
  optsRef.current = opts;

  // Register THIS account's shared MLS UserId so `messagesAdapter` can attribute
  // a sibling device's messages as self via `sender_user_id` — the bulletproof,
  // fetch-free signal (works the instant a message lands, no `/v1/devices` race).
  useEffect(() => {
    if (client === null) return;
    const c = client as unknown as { userId?: () => Promise<Uint8Array | number[]> };
    if (typeof c.userId !== 'function') return;
    let cancelled = false;
    void c
      .userId()
      .then((uid) => {
        if (cancelled) return;
        const bytes = uid instanceof Uint8Array ? uid : new Uint8Array(uid);
        registerCurrentUserMlsUserId(bytesToHex(bytes));
      })
      .catch(() => {
        /* best-effort — device-set fallback still attributes self */
      });
    return () => {
      cancelled = true;
    };
  }, [client]);

  useEffect(() => {
    if (client === null) return;

    const c = client as unknown as ClientWithOnMessage;
    if (typeof c.onMessage !== 'function') {
      log.warn('incomingMessages.no_callback', {
        msg: "SDK exposes no onMessage — incoming bubbles won't render",
      });
      return;
    }

    log.info('incomingMessages.subscribed');

    const unsubscribe = c.onMessage((raw: unknown) => {
      // Read the latest persisters + callbacks off the ref so a fresh inline
      // host callback is always honoured without re-subscribing per render.
      const {
        messages,
        conversations,
        reactions,
        pollVotes,
        meetingEvents,
        saved,
        starred,
        tasks,
        notes,
        reminders,
        readPointers,
        tourSeen,
        onNotify,
        onSelfProfileChanged,
        onConvStatePatch,
      } = optsRef.current;
      void persistIncoming(raw, messages, conversations, c, {
        ...(reactions !== undefined ? { reactions } : {}),
        ...(pollVotes !== undefined ? { pollVotes } : {}),
        ...(meetingEvents !== undefined ? { meetingEvents } : {}),
        ...(saved !== undefined ? { saved } : {}),
        ...(starred !== undefined ? { starred } : {}),
        ...(tasks !== undefined ? { tasks } : {}),
        ...(notes !== undefined ? { notes } : {}),
        ...(reminders !== undefined ? { reminders } : {}),
        ...(readPointers !== undefined ? { readPointers } : {}),
        ...(tourSeen !== undefined ? { tourSeen } : {}),
        ...(onNotify !== undefined ? { onNotify } : {}),
        ...(onSelfProfileChanged !== undefined ? { onSelfProfileChanged } : {}),
        ...(onConvStatePatch !== undefined ? { onConvStatePatch } : {}),
      });
    });

    return () => {
      try {
        unsubscribe();
      } catch (err) {
        log.warn('incomingMessages.unsubscribe.failed', {
          error: (err as Error).message,
        });
      }
    };
    // Only re-subscribe when the SDK client identity changes; all persisters
    // and callbacks are read live from `optsRef` inside the handler above.
  }, [client]);
}

/** Optional sidecar projectors passed through to `persistIncoming`. */
interface IncomingSidecars {
  readonly reactions?: ReactionsPersister;
  readonly pollVotes?: PollVotesPersister;
  readonly meetingEvents?: MeetingEventsPersister;
  readonly saved?: SavedPersister;
  readonly starred?: StarredPersister;
  readonly tasks?: TasksPersister;
  readonly notes?: NotesPersister;
  readonly reminders?: RemindersPersister;
  readonly readPointers?: ReadPointersPersister;
  readonly tourSeen?: TourSeenPersister;
  readonly onNotify?: (evt: IncomingNotification) => void;
  readonly onSelfProfileChanged?: () => void;
  readonly onConvStatePatch?: (
    conversationId: string,
    patch: Record<string, unknown>,
    /** The event's HLC wall-clock (`sentAt`) — a monotonic ordering key so the
     *  host can drop a stale out-of-order patch instead of flip-flopping the
     *  conversation's archive/pin/delete/left state. `0` when unknown (legacy). */
    eventTs: number,
  ) => void;
}

async function persistIncoming(
  raw: unknown,
  messages: MessagesPersister,
  conversations: ConversationsPersister,
  client: ClientWithOnMessage,
  sidecars: IncomingSidecars = {},
): Promise<void> {
  const { reactions, pollVotes, saved, starred, tasks, notes, reminders, readPointers, tourSeen, onNotify, onSelfProfileChanged, onConvStatePatch } = sidecars;
  // Accept both the web (snake_case) and native (camelCase) SDK shapes.
  raw = normalizeIncomingShape(raw);
  try {
    if (!isIncoming(raw)) {
      log.warn('incomingMessages.bad_shape', {
        keys: raw && typeof raw === 'object' ? Object.keys(raw) : null,
      });
      return;
    }

    const conversationIdBytes = toBytes(raw.conversation_id);
    const contentHashBytes = toBytes(raw.content_hash);
    const plaintextBytes = toBytes(raw.plaintext);
    const senderDeviceBytes = toBytes(raw.sender_device);
    const conversationId = bytesToHex(conversationIdBytes);
    const contentHashHex = bytesToHex(contentHashBytes);

    const senderAccountId = bytesToHex(senderDeviceBytes);
    // Account-level MLS UserId of the sender. With shared-identity linking every
    // device of an account carries ONE UserId, so a sibling device's message
    // carries OUR UserId — persisted so the read-side adapter can render it as
    // self via `isSelfMlsUserIdHex` the instant it lands, with no dependency on
    // the `/v1/devices` device-set having loaded. Absent on older SDKs.
    const senderUserIdHex =
      raw.sender_user_id !== undefined && isByteLike(raw.sender_user_id)
        ? bytesToHex(toBytes(raw.sender_user_id))
        : undefined;
    const epoch = typeof raw.epoch === 'bigint' ? Number(raw.epoch) : raw.epoch;
    const sentAt =
      typeof raw.hlc?.wall_ms === 'bigint'
        ? Number(raw.hlc.wall_ms)
        : raw.hlc?.wall_ms ?? Date.now();

    // Decode application envelope. The composer wraps every send as
    //   { kind: "com.ping.text" | "ping.profile" | "com.ping.delivered",
    //     payload: {...} }
    // so the receiver knows whether to render a bubble, update
    // metadata, or stamp a delivery receipt on an existing row.
    const parsed = decodeApplicationEnvelope(plaintextBytes);

    // Delivery-receipt envelope: the peer ACK'd a previously-sent
    // message. Look up the local row by the original content hash
    // (echoed back inside the payload) and stamp `deliveredAt`. We
    // intentionally do NOT insert a bubble row here — the receipt is
    // a sidecar mutation on the original message. Dedupe-by-content-
    // hash on this envelope's own hash is unnecessary; if the BE
    // re-delivers, the second `update` is a no-op stamp of the same
    // timestamp.
    if (parsed?.kind === 'com.ping.delivered') {
      const targetHash =
        typeof parsed.payload?.messageContentHash === 'string'
          ? parsed.payload.messageContentHash
          : undefined;
      const deliveredAtRaw = parsed.payload?.deliveredAt;
      const deliveredAt =
        typeof deliveredAtRaw === 'number'
          ? deliveredAtRaw
          : typeof deliveredAtRaw === 'bigint'
          ? Number(deliveredAtRaw)
          : sentAt;
      if (targetHash && targetHash.length > 0) {
        const target = await messages.getByContentHash(targetHash);
        if (target) {
          await messages.update(target.id, { deliveredAt });
          log.info('incomingMessages.delivered.applied', {
            targetIdPrefix: target.id.slice(0, 8),
            contentHashPrefix: targetHash.slice(0, 8),
            deliveredAt,
          });
        } else {
          log.debug('incomingMessages.delivered.unknown_target', {
            contentHashPrefix: targetHash.slice(0, 8),
          });
        }
      } else {
        log.warn('incomingMessages.delivered.bad_payload');
      }
      return;
    }

    // Read-receipt envelope. Same shape as `com.ping.delivered` but
    // appends to the message's `readBy` array (one entry per peer
    // accountId that has opened the chat). Multi-device readers: a
    // peer with two devices both open the chat → we receive two
    // `com.ping.read` envelopes, both stamp the SAME accountId. The
    // dedupe-by-accountId below prevents the array from growing
    // unboundedly per-device.
    //
    // Wire-format note: per `docs/architecture/types-and-schemas.md`
    // §4.2 the canonical home for these signals is the
    // `EphemeralFrame` channel (typing/presence/read all share an
    // AES-256-GCM key derived via `exportConversationSecret("ping/
    // ephemeral", ...)`). We're shipping the Application-envelope
    // path here because it lands today with existing BE routing +
    // backfill on reconnect (ephemeral frames have no inbox-replay
    // story). Migration path stays open: same payload shape, just
    // a different transport channel.
    if (parsed?.kind === 'com.ping.read') {
      // A `com.ping.read` from one of OUR OWN devices (a linked sibling, whose
      // per-device hash maps to our account) means the account read this chat
      // elsewhere → clear THIS device's unread badge too (multi-device unread
      // sync). Return before the `readBy` block: a sibling's read is not the
      // PEER reading our message, so it must not stamp the read tick.
      if (isSelfDeviceIdHex(senderAccountId)) {
        await conversations
          .update(conversationId, { unreadCount: 0 })
          .catch(() => undefined);
        return;
      }
      const targetHash =
        typeof parsed.payload?.messageContentHash === 'string'
          ? parsed.payload.messageContentHash
          : undefined;
      const readAtRaw = parsed.payload?.readAt;
      const readAt =
        typeof readAtRaw === 'number'
          ? readAtRaw
          : typeof readAtRaw === 'bigint'
          ? Number(readAtRaw)
          : sentAt;
      if (targetHash && targetHash.length > 0) {
        const target = await messages.getByContentHash(targetHash);
        if (target) {
          // Dedupe by accountId — same peer can read the same
          // message from N devices and we want one entry per peer.
          const existing = target.readBy ?? [];
          const filtered = existing.filter(
            (r) => r.accountId !== senderAccountId,
          );
          filtered.push({ accountId: senderAccountId, readAt });
          await messages.update(target.id, { readBy: filtered });
          log.info('incomingMessages.read.applied', {
            targetIdPrefix: target.id.slice(0, 8),
            contentHashPrefix: targetHash.slice(0, 8),
            readers: filtered.length,
          });
        } else {
          log.debug('incomingMessages.read.unknown_target', {
            contentHashPrefix: targetHash.slice(0, 8),
          });
        }
      }
      return;
    }

    // Poll-vote sidecar (§2.8 `0x71 POLL_VOTE`). Like reactions, a vote
    // mutates a separate OR-set projection keyed on the poll's content
    // hash — it must NEVER be persisted as its own bubble (the poll
    // bubble's tally updates in place via `usePollVotes`). Single-choice,
    // latest-wins per actor: `pollVotes.put` is keyed on
    // `(pollTarget, actorAccountId)` so a re-vote replaces the prior row.
    // No-op when the host hasn't wired a `pollVotes` persister.
    if (parsed?.kind === 'com.ping.poll_vote') {
      if (pollVotes) {
        const pollTarget =
          typeof parsed.payload?.target === 'string'
            ? parsed.payload.target
            : typeof parsed.payload?.poll === 'string'
            ? parsed.payload.poll
            : typeof parsed.payload?.poll_id === 'string'
            ? parsed.payload.poll_id
            : undefined;
        // Resolve the actor's FULL selected set. Multi-select ships
        // `choices: number[]`; single-choice ships `option_index`. Registering
        // every choice (not just `choices[0]`) is what makes a PEER's
        // multi-select vote tally correctly instead of collapsing to one option.
        const hasExplicitChoices = Array.isArray(parsed.payload?.choices);
        const choicesRaw: unknown[] = hasExplicitChoices
          ? (parsed.payload?.choices as unknown[])
          : parsed.payload?.option_index !== undefined
          ? [parsed.payload.option_index]
          : [];
        const optionIndices = choicesRaw
          .map((c) =>
            typeof c === 'number' ? c : typeof c === 'bigint' ? Number(c) : undefined,
          )
          .filter((n): n is number => n !== undefined);
        const actorAccountId =
          typeof parsed.payload?.actorAccountId === 'string'
            ? parsed.payload.actorAccountId
            : senderAccountId;
        if (pollTarget && pollTarget.length > 0) {
          if (optionIndices.length > 0) {
            // OR-set REPLACE: the actor's new set supersedes their prior votes.
            // Prefer the atomic path; fall back to delete-then-put per index.
            if (pollVotes.replaceActorVotes) {
              await pollVotes.replaceActorVotes(
                pollTarget,
                actorAccountId,
                optionIndices,
                sentAt,
              );
            } else {
              await pollVotes.deleteByActor(pollTarget, actorAccountId);
              for (const optionIndex of optionIndices) {
                await pollVotes.put({
                  id: `${pollTarget}:${actorAccountId}:${optionIndex}`,
                  pollTarget,
                  actorAccountId,
                  optionIndex,
                  votedAt: sentAt,
                });
              }
            }
            log.info('incomingMessages.poll_vote.applied', {
              pollTargetPrefix: pollTarget.slice(0, 8),
              count: optionIndices.length,
            });
          } else if (hasExplicitChoices) {
            // Explicit EMPTY set → the actor toggled all their choices off; clear.
            await pollVotes.deleteByActor(pollTarget, actorAccountId);
          }
        }
      }
      return;
    }

    // Personal notes + tasks (§2.10, PersonalType 0x90–0x93). These are
    // DeviceGroup-only personal state mirrored across the user's own
    // devices — they NEVER render a chat bubble. Per the doc these are
    // "rejected by reducers if seen in any external conversation": we
    // enforce that here by only applying them when the source is the
    // user's own DeviceGroup. DeviceGroup conversation ids are
    // deterministic with the sentinel prefix `ffdc` (see
    // `device_group_id_for` in ping-core; mirrored by PingTransport's
    // fetchSince skip). A personal envelope arriving on any other
    // conversation is a forged/stray event and is dropped.
    if (
      parsed?.kind === 'com.ping.note_upsert' ||
      parsed?.kind === 'com.ping.note_delete' ||
      parsed?.kind === 'com.ping.task_upsert' ||
      parsed?.kind === 'com.ping.task_delete'
    ) {
      const isDeviceGroup =
        conversationId.length >= 4 &&
        conversationId.slice(0, 4).toLowerCase() === 'ffdc';
      if (!isDeviceGroup) {
        log.warn('incomingMessages.personalState.rejectedNonDeviceGroup', {
          kind: parsed.kind,
          conversationIdPrefix: conversationId.slice(0, 8),
        });
        return;
      }
      if (parsed.kind === 'com.ping.note_upsert' && notes) {
        const noteId =
          typeof parsed.payload?.note_id === 'string'
            ? parsed.payload.note_id
            : typeof parsed.payload?.noteId === 'string'
            ? parsed.payload.noteId
            : undefined;
        if (noteId && noteId.length > 0) {
          const tagsRaw = parsed.payload?.tags;
          const tags = Array.isArray(tagsRaw)
            ? tagsRaw.filter((t): t is string => typeof t === 'string')
            : undefined;
          const updatedAtRaw = parsed.payload?.updated_at ?? parsed.payload?.updatedAt;
          const updatedAt =
            typeof updatedAtRaw === 'number'
              ? updatedAtRaw
              : typeof updatedAtRaw === 'bigint'
              ? Number(updatedAtRaw)
              : sentAt;
          await notes.put({
            id: noteId,
            title:
              typeof parsed.payload?.title === 'string' ? parsed.payload.title : '',
            body: typeof parsed.payload?.body === 'string' ? parsed.payload.body : '',
            updatedAt,
            ...(tags !== undefined && tags.length > 0 ? { tags } : {}),
          });
          log.info('incomingMessages.note_upsert.applied', {
            noteIdPrefix: noteId.slice(0, 8),
          });
        }
      } else if (parsed.kind === 'com.ping.note_delete' && notes) {
        const noteId =
          typeof parsed.payload?.note_id === 'string'
            ? parsed.payload.note_id
            : typeof parsed.payload?.noteId === 'string'
            ? parsed.payload.noteId
            : undefined;
        if (noteId && noteId.length > 0) {
          await notes.delete(noteId);
          log.info('incomingMessages.note_delete.applied', {
            noteIdPrefix: noteId.slice(0, 8),
          });
        }
      } else if (parsed.kind === 'com.ping.task_upsert' && tasks) {
        const taskId =
          typeof parsed.payload?.task_id === 'string'
            ? parsed.payload.task_id
            : typeof parsed.payload?.taskId === 'string'
            ? parsed.payload.taskId
            : undefined;
        if (taskId && taskId.length > 0) {
          const dueRaw = parsed.payload?.due;
          const due =
            typeof dueRaw === 'number'
              ? dueRaw
              : typeof dueRaw === 'bigint'
              ? Number(dueRaw)
              : undefined;
          const done =
            typeof parsed.payload?.done === 'boolean' ? parsed.payload.done : undefined;
          const list =
            typeof parsed.payload?.list === 'string' ? parsed.payload.list : undefined;
          // Richer fields (additive; absent on rows from older devices).
          const priorityRaw = parsed.payload?.priority;
          const priority =
            priorityRaw === 'low' ||
            priorityRaw === 'medium' ||
            priorityRaw === 'high' ||
            priorityRaw === 'urgent'
              ? priorityRaw
              : undefined;
          const stateRaw = parsed.payload?.state;
          const state =
            stateRaw === 'backlog' ||
            stateRaw === 'pending' ||
            stateRaw === 'inProgress' ||
            stateRaw === 'completed' ||
            stateRaw === 'cancelled'
              ? stateRaw
              : undefined;
          const assigneeRaw = parsed.payload?.assignee_id ?? parsed.payload?.assigneeId;
          const assigneeId =
            typeof assigneeRaw === 'string' && assigneeRaw.length > 0
              ? assigneeRaw
              : undefined;
          const description =
            typeof parsed.payload?.description === 'string'
              ? parsed.payload.description
              : undefined;
          // Prefer the sender-stamped `updated_at` (exact cross-device LWW) and
          // fall back to message receive time for legacy senders that omit it —
          // matching the note_upsert path above.
          const taskUpdatedRaw = parsed.payload?.updated_at ?? parsed.payload?.updatedAt;
          const taskUpdatedAt =
            typeof taskUpdatedRaw === 'number'
              ? taskUpdatedRaw
              : typeof taskUpdatedRaw === 'bigint'
              ? Number(taskUpdatedRaw)
              : sentAt;
          await tasks.put({
            id: taskId,
            title:
              typeof parsed.payload?.title === 'string' ? parsed.payload.title : '',
            position:
              typeof parsed.payload?.position === 'string'
                ? parsed.payload.position
                : '',
            updatedAt: taskUpdatedAt,
            ...(due !== undefined ? { due } : {}),
            ...(done !== undefined ? { done } : {}),
            ...(list !== undefined ? { list } : {}),
            ...(priority !== undefined ? { priority } : {}),
            ...(state !== undefined ? { state } : {}),
            ...(assigneeId !== undefined ? { assigneeId } : {}),
            ...(description !== undefined ? { description } : {}),
          });
          log.info('incomingMessages.task_upsert.applied', {
            taskIdPrefix: taskId.slice(0, 8),
          });
        }
      } else if (parsed.kind === 'com.ping.task_delete' && tasks) {
        const taskId =
          typeof parsed.payload?.task_id === 'string'
            ? parsed.payload.task_id
            : typeof parsed.payload?.taskId === 'string'
            ? parsed.payload.taskId
            : undefined;
        if (taskId && taskId.length > 0) {
          await tasks.delete(taskId);
          log.info('incomingMessages.task_delete.applied', {
            taskIdPrefix: taskId.slice(0, 8),
          });
        }
      }
      return;
    }

    // Activity reminders + read pointers (§2.10, PersonalType 0x94/0x95/0x9A).
    // DeviceGroup-only personal state mirrored across the user's own devices —
    // they NEVER render a chat bubble. Same `ffdc` DeviceGroup-origin guard as
    // notes/tasks: a personal envelope on any external conversation is forged
    // and dropped.
    if (
      parsed?.kind === 'com.ping.reminder_set' ||
      parsed?.kind === 'com.ping.reminder_clear' ||
      parsed?.kind === 'com.ping.read_pointer'
    ) {
      const isDeviceGroup =
        conversationId.length >= 4 &&
        conversationId.slice(0, 4).toLowerCase() === 'ffdc';
      if (!isDeviceGroup) {
        log.warn('incomingMessages.personalState.rejectedNonDeviceGroup', {
          kind: parsed.kind,
          conversationIdPrefix: conversationId.slice(0, 8),
        });
        return;
      }
      if (parsed.kind === 'com.ping.reminder_set' && reminders) {
        const p: Record<string, unknown> = parsed.payload ?? {};
        const reminderId =
          typeof p.reminder_id === 'string'
            ? p.reminder_id
            : typeof p.reminderId === 'string'
            ? p.reminderId
            : undefined;
        const convId =
          typeof p.conversation_id === 'string'
            ? p.conversation_id
            : typeof p.conversationId === 'string'
            ? p.conversationId
            : undefined;
        const fireAtRaw = p.fire_at ?? p.fireAt;
        const fireAt =
          typeof fireAtRaw === 'number'
            ? fireAtRaw
            : typeof fireAtRaw === 'bigint'
            ? Number(fireAtRaw)
            : undefined;
        if (
          reminderId &&
          reminderId.length > 0 &&
          convId !== undefined &&
          fireAt !== undefined
        ) {
          const createdRaw = p.created_at ?? p.createdAt;
          const createdAt =
            typeof createdRaw === 'number'
              ? createdRaw
              : typeof createdRaw === 'bigint'
              ? Number(createdRaw)
              : sentAt;
          const messageId =
            typeof p.message_id === 'string'
              ? p.message_id
              : typeof p.messageId === 'string'
              ? p.messageId
              : undefined;
          const note = typeof p.note === 'string' ? p.note : undefined;
          await reminders.put({
            id: reminderId,
            conversationId: convId,
            fireAt,
            createdAt,
            ...(messageId !== undefined ? { messageId } : {}),
            ...(note !== undefined ? { note } : {}),
          });
          log.info('incomingMessages.reminder_set.applied', {
            reminderIdPrefix: reminderId.slice(0, 8),
          });
        }
      } else if (parsed.kind === 'com.ping.reminder_clear' && reminders) {
        const p: Record<string, unknown> = parsed.payload ?? {};
        const reminderId =
          typeof p.reminder_id === 'string'
            ? p.reminder_id
            : typeof p.reminderId === 'string'
            ? p.reminderId
            : undefined;
        if (reminderId && reminderId.length > 0) {
          await reminders.delete(reminderId);
          log.info('incomingMessages.reminder_clear.applied', {
            reminderIdPrefix: reminderId.slice(0, 8),
          });
        }
      } else if (parsed.kind === 'com.ping.read_pointer' && readPointers) {
        const p: Record<string, unknown> = parsed.payload ?? {};
        const convId =
          typeof p.conversation_id === 'string'
            ? p.conversation_id
            : typeof p.conversationId === 'string'
            ? p.conversationId
            : undefined;
        const lastReadRaw = p.last_read_at ?? p.lastReadAt;
        const lastReadAt =
          typeof lastReadRaw === 'number'
            ? lastReadRaw
            : typeof lastReadRaw === 'bigint'
            ? Number(lastReadRaw)
            : undefined;
        if (convId !== undefined && convId.length > 0 && lastReadAt !== undefined) {
          const lastReadMessageId =
            typeof p.last_read_message_id === 'string'
              ? p.last_read_message_id
              : typeof p.lastReadMessageId === 'string'
              ? p.lastReadMessageId
              : undefined;
          await readPointers.put({
            conversationId: convId,
            lastReadAt,
            updatedAt: sentAt,
            ...(lastReadMessageId !== undefined ? { lastReadMessageId } : {}),
          });
          log.info('incomingMessages.read_pointer.applied', {
            convPrefix: convId.slice(0, 8),
          });
        }
      }
      return;
    }

    // Product-tour-seen mirror (§2.10 `0x96 TOUR_SEEN`). DeviceGroup-only
    // personal state mirrored across the user's own devices — NEVER a chat
    // bubble. Same `ffdc` DeviceGroup-origin guard as reminders/read pointers:
    // a personal envelope on any external conversation is forged and dropped.
    if (parsed?.kind === 'com.ping.tour_seen') {
      const isDeviceGroup =
        conversationId.length >= 4 &&
        conversationId.slice(0, 4).toLowerCase() === 'ffdc';
      if (!isDeviceGroup) {
        log.warn('incomingMessages.personalState.rejectedNonDeviceGroup', {
          kind: parsed.kind,
          conversationIdPrefix: conversationId.slice(0, 8),
        });
        return;
      }
      if (tourSeen) {
        const p: Record<string, unknown> = parsed.payload ?? {};
        const tourId =
          typeof p.tour_id === 'string'
            ? p.tour_id
            : typeof p.tourId === 'string'
            ? p.tourId
            : undefined;
        const seenRaw = p.seen_at ?? p.seenAt;
        const seenAt =
          typeof seenRaw === 'number'
            ? seenRaw
            : typeof seenRaw === 'bigint'
            ? Number(seenRaw)
            : sentAt;
        if (tourId && tourId.length > 0) {
          await tourSeen.put({ tourId, seenAt });
          log.info('incomingMessages.tour_seen.applied', {
            tourId,
          });
        }
      }
      return;
    }

    // Self-profile change signal (DeviceGroup-only): one of the account's OTHER
    // devices changed the shared profile (name/avatar). It carries no body —
    // just a nudge for THIS device to re-fetch `/v1/me/profile` so the change
    // reflects in realtime. Same DeviceGroup-origin guard as personal state: a
    // self-profile envelope on any external conversation is forged → dropped.
    if (parsed?.kind === 'com.ping.self_profile') {
      const isDeviceGroup =
        conversationId.length >= 4 &&
        conversationId.slice(0, 4).toLowerCase() === 'ffdc';
      if (isDeviceGroup) {
        onSelfProfileChanged?.();
        log.info('incomingMessages.self_profile.applied');
      } else {
        log.warn('incomingMessages.self_profile.rejectedNonDeviceGroup', {
          conversationIdPrefix: conversationId.slice(0, 8),
        });
      }
      return;
    }

    // Per-conversation personal-state patch (mute/archive/pin/favorite/delete/
    // folder) from another of the account's devices. DeviceGroup-only, same
    // origin guard as personal state — applied to the TARGET conversation row
    // named inside the payload, not to the DeviceGroup conversation itself.
    if (parsed?.kind === 'com.ping.conv_state') {
      const isDeviceGroup =
        conversationId.length >= 4 &&
        conversationId.slice(0, 4).toLowerCase() === 'ffdc';
      if (!isDeviceGroup) {
        log.warn('incomingMessages.conv_state.rejectedNonDeviceGroup', {
          conversationIdPrefix: conversationId.slice(0, 8),
        });
        return;
      }
      const targetId =
        typeof parsed.payload?.conversation_id === 'string'
          ? parsed.payload.conversation_id
          : typeof parsed.payload?.conversationId === 'string'
          ? parsed.payload.conversationId
          : undefined;
      const patch =
        parsed.payload?.patch !== null && typeof parsed.payload?.patch === 'object'
          ? (parsed.payload.patch as Record<string, unknown>)
          : undefined;
      if (targetId && targetId.length > 0 && patch && onConvStatePatch) {
        // `sentAt` (the event's HLC wall-clock) is the monotonic ordering key —
        // the host drops a stale, out-of-order patch instead of flip-flopping
        // archive/pin/delete/left state across the account's devices.
        onConvStatePatch(targetId, patch, sentAt);
        log.info('incomingMessages.conv_state.applied', {
          targetPrefix: targetId.slice(0, 8),
        });
      }
      return;
    }

    // Call signaling beyond invite/end (accept/decline/ringing/hand_raise/
    // reaction/handoff) is TRANSIENT — consumed live by the call store's
    // signaling feed, never a chat bubble. These carry no display body, so
    // without this guard they fall through to `messages.put` and render as a
    // stray EMPTY bubble (notably right when a call ends). call_invite/call_end
    // are intentionally NOT here — they render the call tile.
    if (
      parsed?.kind === 'com.ping.call_accept' ||
      parsed?.kind === 'com.ping.call_decline' ||
      parsed?.kind === 'com.ping.call_ringing' ||
      parsed?.kind === 'com.ping.call_hand_raise' ||
      parsed?.kind === 'com.ping.call_reaction' ||
      parsed?.kind === 'com.ping.call_roster' ||
      parsed?.kind === 'com.ping.call_handoff'
    ) {
      return;
    }

    // Mutation + affordance envelopes (types-and-schemas §2.3 / §5.3).
    // These mutate an EXISTING message (edit/redact) or project a
    // sidecar table (reaction). They must NEVER be persisted as their
    // own bubble — before this branch they fell through to `messages.put`
    // and rendered as a stray empty row.
    //   • com.ping.edit   → replace the target's body in place.
    //   • com.ping.redact → delete the target row (collapse the bubble).
    //   • reaction / reaction_remove → upsert/remove a row in the
    //     optional `reactions` projector (no-op when not wired).
    // Note: pin/unpin fall through to normal message persistence so they
    // render as <EventMessage> bubbles in the chat thread.
    if (
      parsed?.kind === 'com.ping.edit' ||
      parsed?.kind === 'com.ping.redact' ||
      parsed?.kind === 'com.ping.reaction' ||
      parsed?.kind === 'com.ping.reaction_remove'
    ) {
      const targetHash =
        typeof parsed.payload?.target === 'string'
          ? parsed.payload.target
          : undefined;
      if (parsed.kind === 'com.ping.edit' && targetHash) {
        const target = await messages.getByContentHash(targetHash);
        const newBody =
          typeof parsed.payload?.body === 'string'
            ? parsed.payload.body
            : typeof parsed.payload?.new_body === 'string'
            ? parsed.payload.new_body
            : undefined;
        if (target && newBody !== undefined) {
          // Receive-side authorship gate: only the target's author (any of their
          // devices) may edit it. A forged edit from another member is dropped.
          const conv = await conversations.get(conversationId);
          if (!mutationSenderIsAuthor(target, senderAccountId, senderUserIdHex, conv)) {
            log.warn('incomingMessages.edit.rejected_unauthorized', {
              conversationIdPrefix: conversationId.slice(0, 8),
              targetIdPrefix: target.id.slice(0, 8),
            });
            return;
          }
          // Stamp `editedAt` too so the bubble can render its "(edited)"
          // affordance. The field is optional on MessageRecord; the
          // host persister ignores it if it doesn't carry the column.
          const editPatch: Partial<MessageRecord> = {
            body: newBody,
            editedAt: sentAt,
          };
          await messages.update(target.id, editPatch);
          log.info('incomingMessages.edit.applied', {
            targetIdPrefix: target.id.slice(0, 8),
          });
        } else if (newBody !== undefined) {
          // Target hasn't arrived yet (out-of-order) — buffer the edit (with the
          // authenticated sender identity) so it applies, author-verified, the
          // moment the message lands.
          bufferMutation(conversationId, targetHash, {
            kind: 'edit',
            body: newBody,
            sentAt,
            senderDeviceHex: senderAccountId,
            senderUserIdHex,
          });
        }
      } else if (parsed.kind === 'com.ping.redact' && targetHash) {
        const target = await messages.getByContentHash(targetHash);
        if (target) {
          // Receive-side authorship gate: only the target's author (any of their
          // devices) may delete-for-everyone. A forged redact is dropped.
          const conv = await conversations.get(conversationId);
          if (!mutationSenderIsAuthor(target, senderAccountId, senderUserIdHex, conv)) {
            log.warn('incomingMessages.redact.rejected_unauthorized', {
              conversationIdPrefix: conversationId.slice(0, 8),
              targetIdPrefix: target.id.slice(0, 8),
            });
            return;
          }
          await messages.delete(target.id);
          log.info('incomingMessages.redact.applied', {
            targetIdPrefix: target.id.slice(0, 8),
          });
        } else {
          // Target hasn't arrived yet — buffer the redaction (with the
          // authenticated sender identity) so a delete-for-everyone is never lost
          // to reordering, and is author-verified at flush.
          bufferMutation(conversationId, targetHash, {
            kind: 'redact',
            sentAt,
            senderDeviceHex: senderAccountId,
            senderUserIdHex,
          });
        }
      } else if (parsed.kind === 'com.ping.reaction' && targetHash) {
        // Reaction projection is optional — only the hosts that wired a
        // `reactions` persister get the row; everyone else no-ops as
        // before (keeps existing call sites green).
        if (reactions) {
          const emoji =
            typeof parsed.payload?.emoji === 'string'
              ? parsed.payload.emoji
              : undefined;
          const actorAccountId =
            typeof parsed.payload?.actorAccountId === 'string'
              ? parsed.payload.actorAccountId
              : senderAccountId;
          if (emoji && emoji.length > 0) {
            const target = await messages.getByContentHash(targetHash);
            await reactions.put({
              id: `${targetHash}:${emoji}:${actorAccountId}`,
              conversationId,
              targetContentHash: targetHash,
              ...(target?.id !== undefined
                ? { targetMessageId: target.id }
                : {}),
              emoji,
              actorAccountId,
              addedAt: sentAt,
            });
            log.info('incomingMessages.reaction.applied', {
              targetHashPrefix: targetHash.slice(0, 8),
              emoji,
            });
          }
        }
      } else if (parsed.kind === 'com.ping.reaction_remove' && targetHash) {
        if (reactions) {
          const emoji =
            typeof parsed.payload?.emoji === 'string'
              ? parsed.payload.emoji
              : undefined;
          const actorAccountId =
            typeof parsed.payload?.actorAccountId === 'string'
              ? parsed.payload.actorAccountId
              : senderAccountId;
          if (emoji && emoji.length > 0) {
            await reactions.deleteByKey(
              conversationId,
              targetHash,
              emoji,
              actorAccountId,
            );
            log.info('incomingMessages.reaction_remove.applied', {
              targetHashPrefix: targetHash.slice(0, 8),
              emoji,
            });
          }
        }
      }
      return;
    }

    // Meetings (§2.13): ALL FOUR meeting kinds persist as full-envelope
    // timeline rows (they are in FULL_ENVELOPE_BODY_KINDS) and fall through to
    // `messages.put` here — exactly like games (`game_create` + `game_move`/…).
    // The host folds live meeting + RSVP state from the MESSAGES table
    // (`persistedMeetingEvents` in WebChatRoom) and filters `_update`/`_rsvp`/
    // `_cancel` out of the visible timeline (`meetingUpdateIds`). This is the
    // same reliable path text/edits/games use; the old separate-`meetingEvents`-
    // table capture never propagated RSVP/edit to peers, so it is gone.

    // Saved-messages bookmarks (§2.7 `0x62`/`0x63`) — PERSONAL, per-conversation.
    // Apply ONLY from our OWN devices (sibling-apply); a peer's save is private to
    // them, and a forged save from a non-self sender is ignored. Control event —
    // no bubble. This is the BACKGROUND convergence path: it fires regardless of
    // which room is mounted, so a save made on another device shows up on open
    // (the chat-room `WebChatRoom` tap only covered the currently-open room).
    if (parsed?.kind === 'com.ping.save' || parsed?.kind === 'com.ping.unsave') {
      const fromSelf =
        (senderUserIdHex !== undefined && isSelfMlsUserIdHex(senderUserIdHex)) ||
        isSelfDeviceIdHex(senderAccountId);
      const target =
        typeof parsed.payload?.target === 'string' ? parsed.payload.target : undefined;
      if (fromSelf && saved !== undefined && target !== undefined && target.length > 0) {
        try {
          await saved.set(conversationId, target, parsed.kind === 'com.ping.save');
          log.info('incomingMessages.saved.applied', {
            kind: parsed.kind,
            conversationIdPrefix: conversationId.slice(0, 8),
          });
        } catch (err) {
          log.warn('incomingMessages.saved.failed', { err: String(err) });
        }
      }
      return;
    }

    // Starred messages (§2.7 `0x64`) — DeviceGroup-only personal state. Apply
    // `{ target, starred }` (absent `starred` = true) from our OWN DeviceGroup
    // so a star set on another device mirrors here. Control event — no bubble.
    if (parsed?.kind === 'com.ping.star') {
      const fromSelf =
        (senderUserIdHex !== undefined && isSelfMlsUserIdHex(senderUserIdHex)) ||
        isSelfDeviceIdHex(senderAccountId);
      const target =
        typeof parsed.payload?.target === 'string' ? parsed.payload.target : undefined;
      if (
        fromSelf &&
        starred !== undefined &&
        isDeviceGroupConversationId(conversationId) &&
        target !== undefined &&
        target.length > 0
      ) {
        const isStarred = parsed.payload?.starred !== false;
        try {
          await starred.set(target, isStarred);
          log.info('incomingMessages.star.applied', {
            targetPrefix: target.slice(0, 8),
            starred: isStarred,
          });
        } catch (err) {
          log.warn('incomingMessages.star.failed', { err: String(err) });
        }
      }
      return;
    }

    // Disappearing-messages policy: mirror the peer-announced TTL onto the
    // conversation row so this device stamps `expiresAt` on subsequent
    // messages + the local sweep enforces it. Control event — no bubble.
    if (parsed?.kind === 'com.ping.disappearing_policy') {
      const ttl =
        typeof parsed.payload?.ttl_seconds === 'number'
          ? Math.max(0, Math.floor(parsed.payload.ttl_seconds))
          : undefined;
      if (ttl !== undefined) {
        await conversations.update(conversationId, { disappearingTtlSeconds: ttl });
        log.info('incomingMessages.disappearing_policy.applied', {
          conversationIdPrefix: conversationId.slice(0, 8),
          ttl,
        });
      }
      return;
    }

    // Member role change (C3 — client-authoritative, MLS-signed). Apply ONLY if
    // the MLS-AUTHENTICATED sender is the group owner or a current admin. The
    // sender's authority comes from the authenticated sender device → account
    // map (never the spoofable payload), and the OWNER is the authenticated meta
    // inviter stored on the row. Control event — no bubble.
    if (parsed?.kind === 'com.ping.member_role') {
      const targetUserId =
        typeof parsed.payload?.target_user_id === 'string'
          ? parsed.payload.target_user_id
          : undefined;
      const rawRole =
        typeof parsed.payload?.role === 'string' ? parsed.payload.role : undefined;
      const role =
        rawRole === 'owner' || rawRole === 'admin' || rawRole === 'member'
          ? rawRole
          : undefined;
      if (targetUserId !== undefined && role !== undefined) {
        const conv = await conversations.get(conversationId);
        // Resolve the authenticated sender's ACCOUNT id. `senderAccountId` here
        // is actually the sender DEVICE hex (see its declaration); map it to an
        // account via the authenticated roster, or to self.
        const senderAccount = isSelfDeviceIdHex(senderAccountId)
          ? getCurrentUserAccountId()
          : conv?.memberDeviceAccounts?.[senderAccountId];
        const owner = conv?.ownerAccountId;
        const senderAuthorized =
          senderAccount != null &&
          senderAccount.length > 0 &&
          (senderAccount === owner ||
            conv?.memberRoles?.[senderAccount] === 'owner' ||
            conv?.memberRoles?.[senderAccount] === 'admin');
        if (!senderAuthorized) {
          log.warn('incomingMessages.member_role.rejected_unauthorized', {
            conversationIdPrefix: conversationId.slice(0, 8),
          });
          return;
        }
        // The owner's role is immutable — no one can demote the creator.
        if (owner !== undefined && targetUserId === owner && role !== 'owner') {
          return;
        }
        const nextRoles: Record<string, 'owner' | 'admin' | 'member'> = {
          ...(conv?.memberRoles ?? {}),
          [targetUserId]: role,
        };
        await conversations.update(conversationId, { memberRoles: nextRoles });
        log.info('incomingMessages.member_role.applied', {
          conversationIdPrefix: conversationId.slice(0, 8),
          role,
        });
      }
      return;
    }

    // Dedupe a TRUE re-delivery only. `content_hash` is computed over the
    // plaintext ALONE (SHA-256(kind || payload)), so two distinct messages
    // with identical text ("hi", "ok") share a content_hash — deduping on the
    // hash alone silently dropped legitimately-repeated messages. A genuine
    // re-delivery of the SAME envelope also shares the HLC (`sentAt` =
    // hlc.wall_ms), which a repeated message does NOT. So skip only when an
    // existing row matches BOTH the hash and the sentAt; the compound
    // `[contentHash+sentAt]` index (Dexie) is the race-safe backstop on insert.
    const existing = await messages.getByContentHash(contentHashHex);
    if (existing && existing.sentAt === sentAt) {
      log.debug('incomingMessages.dedup', {
        contentHashPrefix: contentHashHex.slice(0, 8),
      });
      return;
    }

    if (parsed?.kind === 'ping.profile') {
      // Profile-intro from the inviter (sent after invite). Direct
      // chats rename the row to the peer; groups merge roster names.
      const inviterAccountId =
        typeof parsed.payload?.accountId === 'string'
          ? parsed.payload.accountId
          : undefined;
      const inviterDisplayName =
        typeof parsed.payload?.displayName === 'string'
          ? parsed.payload.displayName
          : undefined;
      const inviterAvatarMediaId =
        typeof parsed.payload?.avatarMediaId === 'string' &&
        parsed.payload.avatarMediaId.length > 0
          ? parsed.payload.avatarMediaId
          : undefined;

      // Direct-conversation identity, marked `direct: true` by the
      // sender (the post-link admit replay in SettingsModalHost, and
      // NewChatModal). Unlike the legacy single-identity intro handled
      // by the cascade below, this names a 1:1 row after the OTHER
      // party and is multi-device-correct: a profile that describes
      // *our own* account (the peer echoing it, or our own other
      // device's admit replay) is ignored, so we never rename a 1:1
      // after ourselves. This is what lets a freshly LINKED device
      // resolve the peer name that MLS `join` cleared — the historical
      // creation-time intro is never replayed to a late joiner, so the
      // admitting device re-sends the peer identity here.
      if (parsed.payload?.direct === true) {
        const selfAccountId = getCurrentUserAccountId();
        // Record the sender DEVICE → ACCOUNT mapping for EVERY direct profile —
        // including one that describes US (a sibling/linked device of our own
        // account announcing itself via `resendDirectProfileIntro`). The message
        // read-path resolves a sender's device hash → account through this map to
        // render a sibling device's messages as SELF. Must run BEFORE the
        // self-skip below, which would otherwise drop our own device's mapping.
        if (inviterAccountId && inviterAccountId.length > 0 && senderAccountId.length > 0) {
          const convForMap = await conversations.get(conversationId);
          if (
            convForMap &&
            convForMap.memberDeviceAccounts?.[senderAccountId] !== inviterAccountId
          ) {
            await conversations.update(conversationId, {
              memberDeviceAccounts: {
                ...(convForMap.memberDeviceAccounts ?? {}),
                [senderAccountId]: inviterAccountId,
              },
            });
          }
        }
        if (
          inviterAccountId &&
          inviterAccountId.length > 0 &&
          inviterAccountId !== selfAccountId &&
          inviterDisplayName &&
          inviterDisplayName.length > 0
        ) {
          // Resolve the peer's avatar media id → a URL so the 1:1 row shows
          // the peer's photo (a freshly LINKED device has no roster avatar
          // until this arrives). Best-effort: undefined leaves the avatar
          // unset (initials fallback). `avatarUri` is the field every
          // conversation row (direct + group) renders its picture from.
          const avatarUri =
            inviterAvatarMediaId !== undefined
              ? await resolveMediaUrl(inviterAvatarMediaId)
              : undefined;
          const existing = await conversations.get(conversationId);
          if (existing) {
            // Don't clobber a non-default name the user already sees;
            // only fill in the peer name when it's still the placeholder.
            const shouldRename =
              existing.name === undefined ||
              existing.name.length === 0 ||
              existing.name === 'New chat';
            await conversations.update(conversationId, {
              ...(shouldRename ? { name: inviterDisplayName } : {}),
              peerAccountId: inviterAccountId,
              ...(avatarUri !== undefined && avatarUri.length > 0
                ? { avatarUri }
                : {}),
            });
          } else {
            // Profile raced ahead of the Welcome-persist; upsert the
            // row so the name isn't lost. `persistIfNew`'s
            // get-then-return-if-existing preserves this next tick.
            await conversations.put({
              id: conversationId,
              kind: 'direct',
              name: inviterDisplayName,
              peerAccountId: inviterAccountId,
              createdAt: Date.now(),
              joinedViaInvite: true,
              ...(avatarUri !== undefined && avatarUri.length > 0
                ? { avatarUri }
                : {}),
            });
          }
          log.info('incomingMessages.profile.applied.direct', {
            conversationIdPrefix: conversationId.slice(0, 8),
          });
        }
        return;
      }

      const conv = await conversations.get(conversationId);
      if (conv?.kind === 'group') {
        const patch: Partial<ConversationRecord> = {};
        if (inviterAccountId && inviterDisplayName && inviterDisplayName.length > 0) {
          patch.memberDisplayNames = {
            ...(conv.memberDisplayNames ?? {}),
            [inviterAccountId]: inviterDisplayName,
          };
          patch.memberDeviceNames = {
            ...(conv.memberDeviceNames ?? {}),
            [senderAccountId]: inviterDisplayName,
          };
          patch.memberDeviceAccounts = {
            ...(conv.memberDeviceAccounts ?? {}),
            [senderAccountId]: inviterAccountId,
          };
        }
        if (Object.keys(patch).length > 0) {
          await conversations.update(conversationId, patch);
          log.info('incomingMessages.profile.applied.group', {
            conversationIdPrefix: conversationId.slice(0, 8),
          });
        }
      } else if (conv !== undefined) {
        // A BARE `ping.profile` (no `direct: true`) is GROUP roster hydration —
        // every current 1:1 identity intro sets `direct: true` (handled above).
        // It must NEVER rename the conversation or set `peerAccountId`: doing so
        // DOWNGRADES a group into a 1:1 named after the inviter whenever the
        // group classification (MLS meta / `com.ping.group_name`) hasn't landed
        // on the row yet — a timing race with the Welcome that flips a correctly-
        // shown "Yooooo" group to the inviter "Nile2" a second later. Only stash
        // the sender device→name/account mapping (for group bubble attribution)
        // and merge the roster display name; the real title + 1:1 peer come from
        // the MLS meta, `com.ping.group_name`, and the `direct: true` path.
        const patch: Partial<ConversationRecord> = {};
        if (inviterAccountId && inviterDisplayName && inviterDisplayName.length > 0) {
          patch.memberDeviceNames = {
            ...(conv.memberDeviceNames ?? {}),
            [senderAccountId]: inviterDisplayName,
          };
          patch.memberDeviceAccounts = {
            ...(conv.memberDeviceAccounts ?? {}),
            [senderAccountId]: inviterAccountId,
          };
          patch.memberDisplayNames = {
            ...(conv.memberDisplayNames ?? {}),
            [inviterAccountId]: inviterDisplayName,
          };
        }
        if (Object.keys(patch).length > 0) {
          await conversations.update(conversationId, patch);
          log.info('incomingMessages.profile.applied.roster', {
            conversationIdPrefix: conversationId.slice(0, 8),
          });
        }
      }
      // Dedupe the profile-intro via contentHash so the same
      // envelope re-delivered (BE retry, WS reconnect cursor
      // replay) doesn't re-trigger. We DO want to record a
      // settings_kv marker so we don't render a bubble for it
      // either — writing a stub messages row keyed by content hash
      // is overkill; just skip.
      return;
    }

    if (parsed?.kind === 'com.ping.group_name') {
      const patch = groupNamePayloadToConversationPatch(parsed.payload);
      if (patch !== null) {
        const conv = await conversations.get(conversationId);
        if (conv) {
          // RE-INVITE: any account present in this roster broadcast is a current
          // member again — clear it from the "departed" exclusion so it stops
          // being filtered out of the server-roster reconcile.
          const reAdded = new Set(patch.memberAccountIds ?? []);
          const nextDeparted = (conv.departedAccountIds ?? []).filter(
            (id) => !reAdded.has(id),
          );
          const departedChanged =
            nextDeparted.length !== (conv.departedAccountIds ?? []).length;
          await conversations.update(conversationId, {
            ...(conv.memberDisplayNames !== undefined
              ? {
                  memberDisplayNames: {
                    ...conv.memberDisplayNames,
                    ...(patch.memberDisplayNames ?? {}),
                  },
                }
              : patch.memberDisplayNames !== undefined
                ? { memberDisplayNames: patch.memberDisplayNames }
                : {}),
            ...(conv.memberAccountIds !== undefined && patch.memberAccountIds !== undefined
              ? {
                  memberAccountIds: [
                    ...new Set([...conv.memberAccountIds, ...patch.memberAccountIds]),
                  ].sort(),
                }
              : patch.memberAccountIds !== undefined
                ? { memberAccountIds: patch.memberAccountIds }
                : {}),
            ...(departedChanged ? { departedAccountIds: nextDeparted } : {}),
            kind: 'group',
            ...(patch.name !== undefined ? { name: patch.name } : {}),
            // Carry the group avatar id — the web joiner can't read the MLS meta,
            // so this broadcast is the only channel that delivers it.
            ...(patch.avatarMediaId !== undefined ? { avatarMediaId: patch.avatarMediaId } : {}),
            // C: a group must not retain a 1:1 peerAccountId (a single-peer group
            // is a server-side DM, so hydrate may have stamped the sender as a
            // peer). Clear it so the group_name broadcast reclassifies it as a
            // real group instead of leaving it a DM.
            ...(conv.peerAccountId !== undefined && conv.peerAccountId.length > 0
              ? { peerAccountId: '' }
              : {}),
            // Un-hide a soft-deleted group ONLY on a genuine user action — a real
            // rename carries `actorDisplayName`. The SAME `com.ping.group_name`
            // kind is ALSO sent as a SILENT roster/avatar hydration broadcast (no
            // actor); un-hiding on those resurrected a group the user intentionally
            // deleted on every hydration pass (the "deleted group keeps coming
            // back" bug). A genuine RE-ADD still resurfaces via the Welcome →
            // `persistIfNew` live path (which clears deletedAt/leftAt), so gating
            // here loses no legitimate resurface.
            ...(typeof (parsed.payload as { actorDisplayName?: unknown } | undefined)
              ?.actorDisplayName === 'string'
              ? { deletedAt: 0 }
              : {}),
          });
        } else {
          await conversations.put({
            id: conversationId,
            kind: 'group',
            name: patch.name ?? 'Group chat',
            createdAt: Date.now(),
            joinedViaInvite: true,
            ...(patch.memberAccountIds !== undefined
              ? { memberAccountIds: patch.memberAccountIds }
              : {}),
            ...(patch.memberDisplayNames !== undefined
              ? { memberDisplayNames: patch.memberDisplayNames }
              : {}),
            ...(patch.avatarMediaId !== undefined ? { avatarMediaId: patch.avatarMediaId } : {}),
          });
        }
        // Resolve the avatar id → URL right away so the picture shows without
        // waiting for the next hydration pass. Resolve when the row has no
        // avatar yet OR when the media id CHANGED (an admin replaced the icon).
        // The change case is essential for the iOS→web path: iOS ships the new
        // `avatarMediaId` on `com.ping.group_name` (its `com.ping.group_avatar`
        // is an empty tile), and web often can't read the MLS meta — so without
        // resolving here on change, web kept rendering the OLD icon.
        const prevAvatarMediaId = conv?.avatarMediaId;
        if (patch.avatarMediaId !== undefined && patch.avatarMediaId.length > 0) {
          const cur = await conversations.get(conversationId);
          const noAvatarYet = cur === undefined || cur.avatarUri === undefined || cur.avatarUri.length === 0;
          const idChanged = patch.avatarMediaId !== prevAvatarMediaId;
          if (cur !== undefined && (noAvatarYet || idChanged)) {
            const url = withAvatarCacheKey(
              await resolveMediaUrl(patch.avatarMediaId),
              patch.avatarMediaId,
            );
            if (url !== undefined && url.length > 0) {
              await conversations.update(conversationId, { avatarUri: url });
            }
          }
        }
        log.info('incomingMessages.group_name.applied', {
          conversationIdPrefix: conversationId.slice(0, 8),
          avatarMediaIdPresent: patch.avatarMediaId !== undefined,
        });
      }
      // Only persist as an event message bubble when an actor is identified.
      // Hydration sends (add-member / group-create) omit actorDisplayName and
      // must update the row silently — they're metadata sync, not rename actions.
      if (typeof parsed.payload?.actorDisplayName !== 'string') return;
      // Fall through to message persistence.
    }

    // Invite event ("X invited Y"): the reliably-delivered room event now CARRIES
    // the invitee's account id + the full authoritative post-add roster, so it
    // converges every recipient's `memberAccountIds` DIRECTLY. Previously the
    // roster rode only a SEPARATE `com.ping.group_name` broadcast; when that send
    // was dropped but the invite landed, the bubble rendered while the member
    // count stayed stale — two members seeing different counts. Union-merge only
    // (never shrinks; removals ride `com.ping.kick` + the server-roster reconcile).
    // Then fall through so the bubble still renders.
    if (parsed?.kind === 'com.ping.invite') {
      const payload = parsed.payload as
        | {
            inviteeAccountId?: unknown;
            inviteeDisplayName?: unknown;
            memberAccountIds?: unknown;
            memberDisplayNames?: unknown;
          }
        | undefined;
      const self = getCurrentUserAccountId();
      const invitee =
        typeof payload?.inviteeAccountId === 'string' ? payload.inviteeAccountId.trim() : '';
      const rosterRaw = Array.isArray(payload?.memberAccountIds) ? payload.memberAccountIds : [];
      const incomingIds = [
        ...(invitee.length > 0 ? [invitee] : []),
        ...rosterRaw.filter((id): id is string => typeof id === 'string' && id.length > 0),
      ].filter((id) => self === null || id !== self);
      const incomingNames: Record<string, string> = {};
      if (payload?.memberDisplayNames !== undefined && typeof payload.memberDisplayNames === 'object') {
        for (const [id, label] of Object.entries(
          payload.memberDisplayNames as Record<string, unknown>,
        )) {
          if (typeof label === 'string' && label.length > 0) incomingNames[id] = label;
        }
      }
      if (
        invitee.length > 0 &&
        typeof payload?.inviteeDisplayName === 'string' &&
        payload.inviteeDisplayName.length > 0
      ) {
        incomingNames[invitee] = payload.inviteeDisplayName;
      }
      // NB: leaving/re-invite composer reactivation is handled by the EPOCH-gated
      // detector in useIncomingConversations (`persistIfNew`) — NOT here. This
      // message path has no top-level content-hash dedup before dispatch, so a
      // replayed OLD invite (naming self, from the original join) would re-run this
      // handler on every reconnect; clearing `leftAt` here would falsely reactivate
      // the composer. The roster union-merge below is safe (it only ever ADDS).
      if (incomingIds.length > 0) {
        const conv = await conversations.get(conversationId);
        if (conv !== undefined) {
          const prevIds = conv.memberAccountIds ?? [];
          const merged = [...new Set([...prevIds, ...incomingIds])].sort();
          const idsChanged = merged.length !== prevIds.length;
          const reAdded = new Set(incomingIds);
          const nextDeparted = (conv.departedAccountIds ?? []).filter((id) => !reAdded.has(id));
          const departedChanged = nextDeparted.length !== (conv.departedAccountIds ?? []).length;
          const namesChanged = Object.keys(incomingNames).length > 0;
          if (idsChanged || departedChanged || namesChanged) {
            await conversations.update(conversationId, {
              kind: 'group',
              ...(idsChanged ? { memberAccountIds: merged } : {}),
              ...(namesChanged
                ? { memberDisplayNames: { ...(conv.memberDisplayNames ?? {}), ...incomingNames } }
                : {}),
              ...(departedChanged ? { departedAccountIds: nextDeparted } : {}),
            });
            log.info('incomingMessages.invite.rosterMerged', {
              conversationIdPrefix: conversationId.slice(0, 8),
              added: merged.length - prevIds.length,
            });
          }
        }
      }
      // Fall through to event-bubble persistence (the invite carries actorDisplayName).
    }

    // Group avatar: the actor set/replaced the group image. The data/remote URL
    // rides on `blob_ref.url` (same trick GIFs/link previews use). Mirror it onto
    // the local row so the chat-list + header avatars update for every member.
    if (parsed?.kind === 'com.ping.group_avatar') {
      const payload = parsed.payload as
        | { blob_ref?: { url?: unknown }; avatarMediaId?: unknown }
        | undefined;
      const url = typeof payload?.blob_ref?.url === 'string' ? payload.blob_ref.url : undefined;
      // The stable media id, when the sender carries it. CRITICAL: persist it in
      // lock-step with avatarUri — otherwise `hydrateMemberProfiles` re-resolves
      // the row's STALE avatarMediaId on the next pass and REVERTS the icon to
      // the old image (the "X updated the group icon but it never changes" bug).
      const mediaId =
        typeof payload?.avatarMediaId === 'string' && payload.avatarMediaId.length > 0
          ? payload.avatarMediaId
          : undefined;
      // Resolve a URL: prefer the inline one; else resolve from the media id
      // (e.g. an iOS sender that ships only the id).
      let resolvedUrl = url;
      if ((resolvedUrl === undefined || resolvedUrl.length === 0) && mediaId !== undefined) {
        resolvedUrl = await resolveMediaUrl(mediaId);
      }
      if ((resolvedUrl !== undefined && resolvedUrl.length > 0) || mediaId !== undefined) {
        const conv = await conversations.get(conversationId);
        const patch: Partial<ConversationRecord> = {};
        if (resolvedUrl !== undefined && resolvedUrl.length > 0) {
          // Stamp the cache key so a changed icon (new mediaId) always renders
          // fresh instead of being served stale from the browser image cache.
          patch.avatarUri = withAvatarCacheKey(resolvedUrl, mediaId) ?? resolvedUrl;
        }
        if (mediaId !== undefined) patch.avatarMediaId = mediaId;
        if (conv) {
          // RECLASSIFY like the `group_name` handler does. A title-less
          // single-peer group {me, B} sends ONLY `group_avatar` (no group_name),
          // and the web joiner often can't read the MLS meta yet — so the row was
          // seeded as a 1:1 DM with B (`kind:'direct'`, `peerAccountId=B`). If we
          // apply the avatar WITHOUT reclassifying, `hydrateMemberProfiles` keeps
          // re-stamping B's profile avatar onto the row (the 1:1 branch) and the
          // group avatar is clobbered on every pass — the "group + 1:1 show the
          // same avatar until the admin re-uploads" bug. Receiving a group avatar
          // is itself proof this is a GROUP, so mark it durably and drop any stale
          // peer id, exactly like `com.ping.group_name`.
          patch.kind = 'group';
          patch.explicitGroup = true;
          if (conv.peerAccountId !== undefined && conv.peerAccountId.length > 0) {
            patch.peerAccountId = '';
          }
          // Un-hide a soft-deleted group on an avatar change ONLY when it is a
          // genuine actor-driven edit (`actorDisplayName` present) — never on the
          // silent avatar-hydration broadcast, which would resurrect a user-deleted
          // group (mirrors the group_name branch). Genuine re-adds resurface via
          // the Welcome/persistIfNew path.
          if (
            typeof (parsed.payload as { actorDisplayName?: unknown } | undefined)
              ?.actorDisplayName === 'string'
          ) {
            patch.deletedAt = 0;
          }
          await conversations.update(conversationId, patch);
        } else {
          await conversations.put({
            id: conversationId,
            kind: 'group',
            explicitGroup: true,
            name: 'Group chat',
            createdAt: Date.now(),
            joinedViaInvite: true,
            ...patch,
          });
        }
        log.info('incomingMessages.group_avatar.applied', {
          conversationIdPrefix: conversationId.slice(0, 8),
          hasMediaId: mediaId !== undefined,
        });
      }
      // Only persist as an event bubble when an actor is identified (not hydration).
      if (typeof parsed.payload?.actorDisplayName !== 'string') return;
      // Fall through to message persistence.
    }

    // Member removed (com.ping.kick): a peer/admin evicted someone. The MLS
    // Remove Commit already dropped the leaf cryptographically; this app-level
    // mirror drops the now-stale roster entry so the kicked member disappears
    // from the chat-info member list on EVERY surviving member's device. The
    // MLS-meta reconcile (useIncomingConversations) is union-only and can't
    // remove, so without this the kicked member lingered in the member list on
    // peers. Mirrors iOS `applyRemoteMemberRemoval`.
    if (parsed?.kind === 'com.ping.kick') {
      const targetRaw = (parsed.payload as { target_user_id?: unknown } | undefined)
        ?.target_user_id;
      const target = typeof targetRaw === 'string' ? targetRaw.trim() : '';
      const selfAccountId = getCurrentUserAccountId();
      if (target.length > 0 && target !== selfAccountId) {
        const conv = await conversations.get(conversationId);
        if (conv?.memberAccountIds?.includes(target)) {
          const nextDisplayNames = { ...(conv.memberDisplayNames ?? {}) };
          delete nextDisplayNames[target];
          const nextRoles = { ...(conv.memberRoles ?? {}) };
          delete nextRoles[target];
          const nextAvatarUris = { ...(conv.memberAvatarUris ?? {}) };
          delete nextAvatarUris[target];
          // Remember the departure so the authoritative server-roster reconcile
          // (memberProfileHydration) can't RE-ADD this account before their MLS
          // self-remove commits and the server actually drops them.
          const nextDeparted = [
            ...new Set([...(conv.departedAccountIds ?? []), target]),
          ];
          await conversations.update(conversationId, {
            memberAccountIds: conv.memberAccountIds.filter((id) => id !== target),
            departedAccountIds: nextDeparted,
            ...(conv.memberDisplayNames !== undefined
              ? { memberDisplayNames: nextDisplayNames }
              : {}),
            ...(conv.memberRoles !== undefined ? { memberRoles: nextRoles } : {}),
            ...(conv.memberAvatarUris !== undefined
              ? { memberAvatarUris: nextAvatarUris }
              : {}),
          });
          log.info('incomingMessages.kick.rosterPruned', {
            conversationIdPrefix: conversationId.slice(0, 8),
          });
        }
      } else if (target.length > 0 && target === selfAccountId) {
        // SELF was the target: either an admin REMOVED me, or this is my own
        // `reason:'left'` broadcast echoed to a linked sibling device. Mark the
        // group LEFT locally so the composer goes read-only and the row stays
        // visible ("You left"/"You were removed") instead of remaining a normal
        // active group whose every send fails (the SDK dropped the group when it
        // processed my removal Commit). The server already dropped my membership,
        // so no `POST /leave` is issued here. Idempotent: skip if already left.
        const conv = await conversations.get(conversationId);
        if (conv !== undefined && ((conv as { leftAt?: number }).leftAt ?? 0) === 0) {
          await conversations.update(conversationId, { leftAt: Date.now() });
          log.info('incomingMessages.kick.selfLeftMarked', {
            conversationIdPrefix: conversationId.slice(0, 8),
          });
        }
      }
      // Fall through to event-bubble persistence (the kick carries
      // actorDisplayName, so it renders "X removed Y").
    }

    const body = parsed?.body;

    // A plaintext that yields NO renderable body is a control/commit frame — a
    // GroupContextExtensions rename/avatar commit (`setConversationName`) that
    // surfaced on the message stream, or an empty/unrecognized payload. It is
    // NOT a chat message; persisting it produced the empty "ghost" bubbles
    // (sender name + timestamp, no content). Skip it entirely.
    //
    // This does NOT affect room events that SHOULD render: every renderable kind
    // — text, media, AND every room-event kind in `FULL_ENVELOPE_BODY_KINDS`
    // (group_name / group_avatar / invite / kick / pin / …) — carries a defined
    // `body` (the envelope JSON) out of `decodeApplicationEnvelope`. So a room
    // event ALWAYS persists with its envelope body and ALWAYS renders as the
    // EventMessage tile via `deriveVariantFromBody`; that function only ever
    // returns `undefined` for these body-less control frames, which we now drop
    // before they can become a bubble.
    if (body === undefined || body.length === 0) {
      return;
    }

    const id =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `incoming-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    // Reply reconstruction. When the sender threaded this message the
    // JSON envelope carries a top-level `parent_id` (the parent's
    // content hash). Resolve the local parent row + its author's
    // display name so the bubble can render the quoted reply card.
    // `replyPreview` from the payload is preferred over a body-derived
    // preview because the sender already truncated/formatted it. A
    // missing parent row (we never received it) still yields a card
    // with whatever preview the sender embedded.
    const parentId = parsed?.parentId;
    let inReplyTo:
      | { messageId: string; preview: string; senderName?: string }
      | undefined;
    if (parentId) {
      const replyPreview =
        typeof parsed?.payload?.replyPreview === 'string'
          ? parsed.payload.replyPreview
          : undefined;
      const parent = await messages.getByContentHash(parentId);
      let senderName: string | undefined;
      if (parent) {
        const selfAccountId = getCurrentUserAccountId();
        const parentIsSelf =
          selfAccountId !== null && parent.senderAccountId === selfAccountId;
        if (!parentIsSelf) {
          const conv = await conversations.get(conversationId);
          senderName =
            conv?.memberDisplayNames?.[parent.senderAccountId] ??
            conv?.memberDeviceNames?.[parent.senderAccountId];
        }
      }
      const preview =
        replyPreview ?? lastMessagePreview(parent?.body) ?? '';
      inReplyTo = {
        messageId: parent?.id ?? '',
        preview,
        ...(senderName !== undefined ? { senderName } : {}),
      };
    }

    // @mentions carried on the text envelope (§2.2 TextBody.mentions).
    // The wire shape is `payload.mentions: [{ user_id, display_name,
    // range }]` (readMentions also accepts userId/displayName). Project
    // down to the bare userId list the read-side `Message.mentions`
    // expects so the bubble can render @mention pills. Guarded/additive —
    // non-text envelopes and senders that omit mentions yield no field.
    let mentionUserIds: string[] | undefined;
    // Mention display names — needed to detect a self-mention in a 1:1, where
    // the mention `user_id` is the (per-side) conversation id, not an account
    // id, so the only reliable "you were tagged" signal is the display name.
    let mentionDisplayNames: string[] | undefined;
    const rawMentions = parsed?.payload?.mentions;
    if (Array.isArray(rawMentions)) {
      const ids = rawMentions
        .map((m) => {
          if (!m || typeof m !== 'object') return undefined;
          const r = m as Record<string, unknown>;
          const id = r.user_id ?? r.userId;
          return typeof id === 'string' && id.length > 0 ? id : undefined;
        })
        .filter((id): id is string => id !== undefined);
      if (ids.length > 0) mentionUserIds = ids;
      const names = rawMentions
        .map((m) => {
          if (!m || typeof m !== 'object') return undefined;
          const r = m as Record<string, unknown>;
          const name = r.display_name ?? r.displayName;
          return typeof name === 'string' && name.trim().length > 0
            ? name.trim()
            : undefined;
        })
        .filter((n): n is string => n !== undefined);
      if (names.length > 0) mentionDisplayNames = names;
    }

    // Disappearing-messages: if the conversation carries a TTL, stamp the
    // expiry so the local sweep can hard-delete this row once it lapses.
    const ttlConv = await conversations.get(conversationId);
    const ttlSeconds = ttlConv?.disappearingTtlSeconds ?? 0;
    const expiresAt = ttlSeconds > 0 ? sentAt + ttlSeconds * 1000 : undefined;

    const record: MessageRecord = {
      id,
      conversationId,
      epoch,
      // Use the HLC wall clock so incoming messages sort
      // monotonically with the receiver's own local-echo'd sends
      // (which also use Date.now() as their serverSeq).
      serverSeq: sentAt,
      contentHash: contentHashHex,
      senderAccountId,
      ...(senderUserIdHex !== undefined ? { senderUserIdHex } : {}),
      ...(body !== undefined ? { body } : {}),
      // [#2] Keep the raw AppEvent bytes so this message can be replayed
      // faithfully to a future linked device via peer history-transfer.
      appEventBytes: plaintextBytes,
      sentAt,
      ...(inReplyTo !== undefined ? { inReplyTo } : {}),
      ...(mentionUserIds !== undefined ? { mentions: mentionUserIds } : {}),
      ...(mentionDisplayNames !== undefined ? { mentionNames: mentionDisplayNames } : {}),
      ...(expiresAt !== undefined ? { expiresAt } : {}),
    };

    try {
      await messages.put(record);
    } catch (putErr) {
      // The `[contentHash+sentAt]` unique index rejects a TRUE re-delivery
      // that raced past the pre-check above — that's the intended idempotency,
      // not an error. Swallow the constraint violation; rethrow anything else.
      const name = (putErr as { name?: string })?.name ?? '';
      if (name === 'ConstraintError') {
        log.debug('incomingMessages.dedup.constraint', {
          contentHashPrefix: contentHashHex.slice(0, 8),
        });
        return;
      }
      throw putErr;
    }

    // The target of any earlier-arrived edit/redact just landed — apply the
    // buffered mutation now (author-verified against this row) so an out-of-order
    // edit/redaction is never lost, and a forged one never wins the race.
    // `applyPendingMutations` fetches the conversation ONLY when a buffered
    // mutation actually exists (empty-buffer fast path first), so a normal
    // message incurs no extra DB read.
    await applyPendingMutations(
      messages,
      conversations,
      conversationId,
      contentHashHex,
      record,
    );

    // Bump the conversation's lastMessageAt + push a fresh preview +
    // sender so the chat-list row's "John: hey, are you free?"
    // subtitle updates in real time as peer messages arrive. The
    // preview formatter is the same one used by the outgoing
    // (`persistSentMessage`) path so self vs peer previews are
    // byte-identical for the same source text.
    //
    // Note: pure-payload envelopes (voice / gif / attachments) store
    // the full envelope JSON as `body`; `lastMessagePreview` will
    // include the JSON literal, which isn't ideal — but the chat
    // surface's own message-variants dispatcher swaps the row's
    // subtitle to a media glyph anyway, so the user never sees the
    // raw JSON. A future refactor can decouple the preview by
    // computing it off the parsed envelope's kind instead.
    const preview = lastMessagePreview(body);
    let conv = await conversations.get(conversationId);
    if (
      conv === undefined &&
      !isDeviceGroupConversationId(conversationId) &&
      !isRegisteredCallGroup(conversationId)
    ) {
      // MESSAGE-BEFORE-ROW: the first message of a brand-new chat can win the
      // race against the Welcome / `persistIfNew` that creates the conversation
      // row. Insert a minimal placeholder now so the chat SURFACES immediately —
      // otherwise the message is stored but invisible until a later reconcile /
      // reload. `persistIfNew`'s anti-clobber + upgrade path then fills in the
      // real kind/name/roster (incl. direct→group) once the MLS meta lands.
      // Re-check immediately before the insert so we never clobber a row a
      // concurrent `persistIfNew` just created (device/call groups are excluded
      // above — they must never surface as chats).
      const raced = await conversations.get(conversationId);
      if (raced === undefined) {
        await conversations.put({
          id: conversationId,
          kind: 'direct',
          name: 'New chat',
          createdAt: sentAt,
          joinedViaInvite: true,
        });
      }
      conv = await conversations.get(conversationId);
    }
    if (conv) {
      // A message authored by OUR OWN account — this device's echo OR a linked
      // SIBLING device (shared MLS UserId, or a device hex in our own device-set)
      // — must NOT bump this device's unread badge: we are the sender, not a
      // reader with something new to see. Only genuine PEER messages increment.
      // (`isSelfDeviceIdHex` matches any of the account's devices once /v1/devices
      // resolves; `isSelfMlsUserIdHex` is the fetch-free shared-identity signal.)
      const isSelfSender =
        isSelfDeviceIdHex(senderAccountId) ||
        (senderUserIdHex !== undefined && isSelfMlsUserIdHex(senderUserIdHex));
      // Persist the sender's ACCOUNT id (not the raw device hex) as the chat-list
      // preview attribution key. `senderAccountId` above is actually the sender's
      // DEVICE hex; the chat-list "Alice: hi" preview keys on account ids
      // (`memberDisplayNames`, which hydration reconciles authoritatively from
      // `GET /members` + the directory), so resolving device→account HERE makes the
      // preview resolve via the reliable account map instead of the best-effort
      // device map — and renders a sibling device's message as "You:". Falls back
      // to the device hex when the account isn't mapped yet (the device-map bridge
      // in `resolveGroupSenderLabel` + a later hydration pass still resolve it).
      const selfAccountForPreview = getCurrentUserAccountId();
      const previewSenderId = isSelfSender
        ? selfAccountForPreview ?? senderAccountId
        : conv.memberDeviceAccounts?.[senderAccountId] ?? senderAccountId;
      // Bump the unread counter for a PEER message only. The chat list surfaces it
      // as a badge; viewing the conversation resets it to 0 (WebChatRoom's
      // view-effect).
      const prevUnread =
        typeof (conv as { unreadCount?: number }).unreadCount === 'number'
          ? (conv as { unreadCount?: number }).unreadCount ?? 0
          : 0;
      await conversations.update(conversationId, {
        lastMessageAt: sentAt,
        ...(isSelfSender ? {} : { unreadCount: prevUnread + 1 }),
        ...(preview !== undefined ? { lastMessagePreview: preview } : {}),
        lastMessageSenderAccountId: previewSenderId,
        // Un-hide a soft-deleted chat: a new peer message resumes it with its
        // preserved identity (name/avatar/kind/roster) intact. Set
        // UNCONDITIONALLY (0 = visible) — some `ConversationsPersister.get`
        // implementations project a subset that omits `deletedAt`, so we can't
        // gate on the read value. A no-op for non-deleted rows.
        deletedAt: 0,
        // REVIVE an orphaned chat too. `orphanedAt` is the SERVER-authoritative
        // 404 marker that a re-emitted Welcome must NOT clear (that flapped the
        // ghost row every reconnect) — but a real inbound CONTENT message is the
        // one signal that proves the MLS group is alive again, so it legitimately
        // revives. This is the message-side counterpart to the server-relist
        // revive in `bootstrapConversationsFromServer`. Unconditional (0 = live)
        // for the same projection reason as `deletedAt` above.
        orphanedAt: 0,
      });
    }

    // Emit a notification event for this NEW inbound message. Fired even when
    // the conversation row doesn't exist YET (a message that arrives before
    // its Welcome/`persistIfNew` lands — common for the FIRST message of a
    // brand-new chat) so that first message isn't silently missed; we just
    // have less name context in that case. The platform (web/desktop) decides
    // whether to actually banner/sound/OS-notify based on focus, the active
    // conversation, mute, and preferences.
    // Meeting sidecars (`_update`/`_rsvp`/`_cancel`) persist as rows so they fold
    // into the card from the messages table (like game moves), but they are NOT
    // a "new message" — they mutate the existing meeting card in place. Never
    // banner/sound/OS-notify for them (an RSVP or edit must not read as a chat).
    const isMeetingSidecar =
      parsed?.kind === 'com.ping.meeting_update' ||
      parsed?.kind === 'com.ping.meeting_rsvp' ||
      parsed?.kind === 'com.ping.meeting_cancel';
    if (onNotify !== undefined && !isMeetingSidecar) {
      const me = getCurrentUserAccountId();
      const myName = getCurrentUserDisplayName();
      const hasMentions = mentionUserIds !== undefined && mentionUserIds.length > 0;
      // STRICT self-mention detection — only when the message actually tags
      // YOU (so the mention chime never fires for a tag of someone else):
      //   • account-id match (groups carry the member accountId as `user_id`), OR
      //   • display-name match (1:1 chats carry the conversation id as `user_id`,
      //     not an account id, so the reliable signal is the mention's
      //     `display_name` equalling the local user's name).
      const accountMatch = me !== null && mentionUserIds?.includes(me) === true;
      const nameMatch =
        myName !== null &&
        mentionDisplayNames?.some((n) => n.toLowerCase() === myName.toLowerCase()) === true;
      const isMention = hasMentions && (accountMatch || nameMatch);
      const senderName =
        conv?.memberDisplayNames?.[senderAccountId] ??
        conv?.memberDeviceNames?.[senderAccountId];
      const evt: IncomingNotification = {
        kind: isMention ? 'mention' : 'message',
        conversationId,
        isMention,
        sentAt,
        senderId: senderAccountId,
        ...(conv?.name !== undefined ? { conversationName: conv.name } : {}),
        ...(senderName !== undefined ? { senderName } : {}),
        ...(preview !== undefined ? { preview } : {}),
      };
      try {
        onNotify(evt);
      } catch (err) {
        log.warn('incomingMessages.notify.failed', {
          error: (err as Error).message,
        });
      }
    }

    log.info('incomingMessages.persist', {
      conversationIdPrefix: conversationId.slice(0, 8),
      contentHashPrefix: contentHashHex.slice(0, 8),
      epoch,
      bodyLen: body?.length ?? 0,
    });

    // Fire-and-forget delivery ACK back to the conversation. The
    // sender's own `com.ping.delivered` handler (above) will stamp
    // `deliveredAt` on the matching MessageRecord, which the
    // MessageBubble renders as the muted double-check glyph. Failure
    // here is non-fatal — receipts are a UX nicety, not a correctness
    // invariant; the bubble just stays in the "sent" state.
    if (typeof client.getConversation === 'function') {
      const ackEnvelope = {
        kind: 'com.ping.delivered',
        payload: {
          messageContentHash: contentHashHex,
          deliveredAt: Date.now(),
        },
      };
      void (async () => {
        try {
          const conv = client.getConversation!(conversationIdBytes);
          await conv.send(new TextEncoder().encode(JSON.stringify(ackEnvelope)));
        } catch (err) {
          log.debug('incomingMessages.delivered.ack_failed', {
            contentHashPrefix: contentHashHex.slice(0, 8),
            error: (err as Error).message,
          });
        }
      })();
    }
  } catch (err) {
    log.error('incomingMessages.persist.failed', err as Error);
  }
}

function isIncoming(raw: unknown): raw is IncomingMessageLike {
  if (!raw || typeof raw !== 'object') return false;
  const r = raw as Record<string, unknown>;
  return (
    isByteLike(r.conversation_id) &&
    isByteLike(r.content_hash) &&
    isByteLike(r.sender_device) &&
    isByteLike(r.plaintext)
  );
}

/**
 * Normalize an incoming message to the snake_case shape this consumer
 * expects.
 *
 * The web WASM SDK emits snake_case (`conversation_id` / `sender_device`
 * / `content_hash`, `hlc.wall_ms`). The RN-macOS binding emits camelCase
 * (`conversationId` / `senderDevice` / `contentHash`, `hlc.wallMs`) —
 * `persistIncoming` was written against the web shape, so a native
 * message would fail `isIncoming` (dropped as `bad_shape`) and, even if
 * it passed, would lose its timestamp (`hlc.wall_ms` undefined → falls
 * back to `Date.now()`). Map the native shape so a single code path
 * serves both platforms. `plaintext` / `epoch` already match.
 */
function normalizeIncomingShape(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object') return raw;
  const r = raw as Record<string, unknown>;
  // Web WASM shape already — leave untouched.
  if (!('conversationId' in r)) return raw;
  const hlc = r.hlc as
    | { wall_ms?: number; wallMs?: number; logical?: number }
    | undefined;
  return {
    ...r,
    conversation_id: r.conversationId,
    sender_device: r.senderDevice,
    ...(r.senderUserId !== undefined ? { sender_user_id: r.senderUserId } : {}),
    content_hash: r.contentHash,
    hlc: hlc
      ? { wall_ms: hlc.wall_ms ?? hlc.wallMs ?? 0, logical: hlc.logical ?? 0 }
      : r.hlc,
  };
}

function isByteLike(v: unknown): v is ByteLike {
  if (v instanceof Uint8Array) return true;
  if (Array.isArray(v)) {
    // Defensively check the first element is a number — `Vec<u8>`
    // through serde_wasm_bindgen always produces a uniform numeric
    // array, so checking the head is sufficient.
    return v.length === 0 || typeof v[0] === 'number';
  }
  return false;
}

function toBytes(v: ByteLike): Uint8Array {
  if (v instanceof Uint8Array) return v;
  return new Uint8Array(v);
}

interface ParsedAppEnvelope {
  kind: string | undefined;
  payload: Record<string, unknown> | undefined;
  body: string | undefined;
  /** Top-level `parent_id` from the JSON envelope (the parent message's
   * content hash) when the sender threaded this as a reply. Undefined
   * for non-reply envelopes. Consumed by the messages.put path to
   * reconstruct `MessageRecord.inReplyTo`. */
  parentId: string | undefined;
}

// Media / structured envelopes whose FULL JSON is persisted as the
// row `body` so the chat surface's per-message variant dispatcher
// (`deriveVariant`) can decode them client-side without a sidecar
// table. Everything here renders as a real bubble (not a metadata
// mutation) and flows through this hook's `getByContentHash` dedupe
// gate, so BE retries / WS reconnect replays are ignored.
const FULL_ENVELOPE_BODY_KINDS = new Set<string>([
  'com.ping.voice',
  'com.ping.gif',
  'com.ping.image',
  'com.ping.video',
  'com.ping.file',
  'com.ping.location',
  'com.ping.link_preview',
  'com.ping.poll',
  // Meeting card (§2.13). ALL FOUR meeting kinds persist the full envelope as a
  // timeline row — exactly like games (`game_create` + `game_move`/…). The
  // create renders the card; `_update`/`_rsvp`/`_cancel` are FILTERED OUT of the
  // visible timeline (WebChatRoom `meetingUpdateIds`) and fold into the live
  // `meetingState` FROM THE MESSAGES TABLE (`persistedMeetingEvents`) — the same
  // reliable delivery path text/edits/games use. The earlier design captured the
  // sidecars into a separate `meetingEvents` table, which did not propagate RSVP/
  // edit to peers; folding from messages fixes that. Without these entries a
  // sidecar decoded to an undefined body and was dropped before `messages.put`.
  'com.ping.meeting',
  'com.ping.meeting_update',
  'com.ping.meeting_rsvp',
  'com.ping.meeting_cancel',
  // Call lifecycle tiles (Figma 2701:6668) — persist the full envelope so
  // deriveVariant renders the ongoing/ended/missed CallNotificationCard.
  'com.ping.call_invite',
  'com.ping.call_end',
  'com.ping.game_create',
  'com.ping.game_move',
  'com.ping.game_outcome',
  // Thread-root marker: the sender emits this once before the first thread
  // reply so receivers can anchor the thread. It carries a top-level
  // `parent_id` (= the root message's content hash) that the read-side
  // (WebChatRoom's thread-affordance memo) needs to fold replies under the
  // root and keep them OUT of the main timeline. We persist the FULL
  // envelope JSON as the row body so `parseEnvelope(body).parent_id` round-
  // trips — a plain/empty body would lose the link. The marker never
  // renders a bubble (deriveVariant/formatPreview both ignore it).
  'com.ping.thread_root',
  // Forwarded message — a first-class `com.ping.forward` envelope wrapping the
  // original content. Persist the FULL envelope JSON as the row body so the
  // recipient actually STORES it (without this it decoded to an undefined body
  // and got dropped before `messages.put`, so the person a message was
  // forwarded to received nothing). The messages adapter (`unwrapForward`) then
  // reduces it to the inner content + the "(forwarded)" marker — identically on
  // the forwarder's own echo and every recipient's copy.
  'com.ping.forward',
  // Group-join request (owner-approval invite flow). NOT a registered AppEvent
  // kind, so persist the FULL envelope JSON as the row body — the read side
  // (`decodeGroupJoinRequest` in WebChatRoom) reads it to fold the message out of
  // the timeline and drive the Approve banner. Without this the receiver would
  // store an empty body and the approver would never see the request.
  'com.ping.group_join_request',
  // Room event kinds that render as <EventMessage> bubbles in the chat thread
  // (arch table §3). Full JSON body lets deriveVariant identify the kind and
  // read the actor/invitee/target display names from the payload.
  'com.ping.invite',
  'com.ping.kick',
  'com.ping.group_topic',
  'com.ping.group_name',
  'com.ping.group_avatar',
  'com.ping.pin',
  'com.ping.unpin',
]);

function decodeApplicationEnvelope(bytes: Uint8Array): ParsedAppEnvelope | undefined {
  try {
    const text = new TextDecoder().decode(bytes);
    const parsed = JSON.parse(text) as {
      kind?: string;
      payload?: Record<string, unknown>;
      parent_id?: unknown;
    };
    const kind = typeof parsed.kind === 'string' ? parsed.kind : undefined;
    const payload =
      parsed.payload && typeof parsed.payload === 'object' ? parsed.payload : undefined;
    const parentId =
      typeof parsed.parent_id === 'string' && parsed.parent_id.length > 0
        ? parsed.parent_id
        : undefined;
    let body: string | undefined;
    if (kind === 'com.ping.text' && typeof payload?.body === 'string') {
      // Plain text → store the bare display string. But a text REPLY
      // (carries a top-level `parent_id`) must keep the FULL envelope so
      // `parent_id` survives — the read-side thread-affordance memo folds
      // replies under their root and keeps them OUT of the receiver's main
      // timeline by `parseEnvelope(body).parent_id`. Without this the
      // receiver dropped `parent_id` and the reply leaked into the main
      // chat with no "N replies" card. Rendering (`decodeBubbleBody`) and
      // the chat-list preview (`lastMessagePreview`) both decode the
      // envelope form transparently, so plain reads are unaffected.
      // Keep the FULL envelope when the text carries a `parent_id` (reply
      // threading, see above), an embedded `link_preview` (so the receiver's
      // bubble renders the unified text + preview card), OR ranged @MENTIONS.
      // Mentions must keep the envelope because `decodeBubbleMentions` reads the
      // properly-PAIRED `{user_id, display_name}` ranges from it — storing the
      // bare string dropped them, so the receiver's tile fell back to a
      // single-word `@handle` regex and a multi-word mention like "@30 Gideon"
      // rendered as just "@30" (the self bubble kept the envelope and looked
      // correct — the reported self-vs-other mention asymmetry). The stored flat
      // `mentions`/`mentionNames` fields can't substitute: they're filtered
      // independently and may misalign. A plain text with none stores the bare
      // display string.
      const hasRangedMentions =
        Array.isArray(payload.mentions) && payload.mentions.length > 0;
      body =
        parentId !== undefined ||
        payload.link_preview !== undefined ||
        hasRangedMentions
          ? text
          : payload.body;
    } else if (kind !== undefined && FULL_ENVELOPE_BODY_KINDS.has(kind)) {
      // Persist the FULL envelope JSON as the row's `body` so the chat
      // surface's variant dispatcher can decode it client-side.
      body = text;
    } else if (!kind) {
      // Bare-text fallback for any sender that doesn't follow the
      // envelope shape — render the decoded utf-8 as the body.
      body = text;
    }
    return { kind, payload, body, parentId };
  } catch {
    return undefined;
  }
}

function bytesToHex(b: Uint8Array): string {
  let out = '';
  for (let i = 0; i < b.length; i++) {
    out += (b[i] ?? 0).toString(16).padStart(2, '0');
  }
  return out;
}
